import { Col, Form, Modal, Row, message } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { omit, pick } from 'lodash';
import React, { useContext, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputPassword from 'components/atoms/InputPassword';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import { Context, defaultStoreState } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';

import logoutApi from 'services/logout';
import zmenaUdajuApi, { IUdaje } from 'services/zmenaUdaju';
import zneaktivneniUctuApi, { EZnekativneniStav } from 'services/zneaktivneniUctu';

import { ENotificationType } from 'enums/notificationType';
import routePathEnum from 'enums/routePathEnum';

import getLanguageObject from 'utils/getLanguageObject';
import phoneValidation from 'utils/validations/phoneValidation';

interface IMujProfil {}

const MujProfil: React.FC<IMujProfil> = () => {
    const [form] = Form.useForm();
    const [formModal] = Form.useForm();
    const { profily, language, NotificationComponent, setStoreState } = useContext(Context);
    const [loading, setLoading] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loadingModal, setLoadingModal] = useState<boolean>(false);
    const [showPwErr, setShowPwErr] = useState<boolean>(false);
    const { navigateWorkaround } = useNavigateWorkaround();
    const [visible, setVisible] = useState<boolean>(false);

    // Pokud se lisi hodnoty ve formulari s hodnotama ve Store (profily) zavola se sluzba zmenaUdajuApi
    const onFinish = (value: Store) => {
        setLoading(true);
        const omitValue = omit(value, ['telefon2', 'email2', 'typprihlaseni']); // Tyto hodnoty jsou ve formulari, ale neposilaji se. dunno?!?
        const udajeValue: IUdaje = pick(omitValue, ['jmeno', 'prijmeni', 'telefon', 'email']);
        zmenaUdajuApi(udajeValue, language)
            .then(() => {
                return NotificationComponent({
                    type: ENotificationType.SUCCESS,
                    title: 'ZMENA_UDAJU_SUCESS',
                    description: ''
                });
            })
            .catch(() => {
                NotificationComponent({
                    type: ENotificationType.ERROR,
                    title: 'ZMENA_UDAJU_FAILED',
                    description: ''
                });
            })
            .finally(() => {
                setLoading(false);
                setIsEdit(false);
            });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinishModal = (vals: any) => {
        if (profily?.id) {
            setLoadingModal(true);
            zneaktivneniUctuApi(profily?.id, vals.password)
                .then(resp =>
                    logoutApi().then(() => {
                        setStoreState &&
                            setStoreState({
                                ...defaultStoreState,
                                language: language
                            });
                        navigateWorkaround(routePathEnum.LOGIN_ROZCESTNIK);
                        setLoadingModal(false);
                        setVisible(false);
                        message.success(
                            <TranslateText>
                                {resp.stav === EZnekativneniStav.SMAZAN
                                    ? 'ZRUSIT_UCET_SUCCESS'
                                    : 'ZNEAKTIVNIT_UCET_SUCCESS'}
                            </TranslateText>
                        );
                    })
                )
                .catch(error => {
                    setLoadingModal(false);
                    error.response.status === 401
                        ? setShowPwErr(true)
                        : NotificationComponent({
                              type: ENotificationType.ERROR,
                              title: 'ZMENA_UDAJU_FAILED'
                          });
                });
        }
    };
    return (
        <FormWrapper onFinish={onFinish} outForm={form}>
            <Title level={3} text="MUJ_PROFIL" />
            <Title level={5} text="REGISTRACE_NADPIS" />
            {profily?.stavudaju === 'CHYBA' ? (
                <Paragraph
                    text={'PROFIL_UDAJE_CHYBA'}
                    style={{
                        display: 'block',
                        paddingTop: 20,
                        paddingBottom: 20
                    }}
                />
            ) : (
                <>
                    <InputComponent label="JMENO" name="jmeno" initialValue={profily?.jmeno} disabled={!isEdit} />
                    <InputComponent
                        label="PRIJMENI"
                        name="prijmeni"
                        initialValue={profily?.prijmeni}
                        disabled={!isEdit}
                    />
                    <Title level={5} text="KONTAKTNI_UDAJE" dividerTitle={true} />
                    <TranslateText>KONTAKTNI_UDAJE_INFO</TranslateText>
                    {!isEdit && (
                        <>
                            <InputComponent
                                label="EMAIL"
                                name="email"
                                type="email"
                                initialValue={profily?.email}
                                disabled={true}
                            />
                            {profily?.email2 && (
                                <InputComponent
                                    label="EMAIL"
                                    name="email2"
                                    type="email2"
                                    initialValue={profily?.email2}
                                    disabled={true}
                                    notRequired={true}
                                />
                            )}
                        </>
                    )}
                    <InputComponent
                        label="TELEFON"
                        name="telefon"
                        type="tel"
                        initialValue={profily?.telefon1}
                        disabled={!isEdit}
                        rules={phoneValidation()}
                    />
                    {profily?.telefon2 && (
                        <InputComponent
                            label="TELEFON_2"
                            name="telefon2"
                            type="tel2"
                            initialValue={profily?.telefon2}
                            disabled={!isEdit}
                            rules={phoneValidation()}
                        />
                    )}
                    {!isEdit && (
                        <>
                            <Title level={5} text="DALSI_UDAJE" dividerTitle={true} />
                            {profily?.posledniprihlaseni && (
                                <InputComponent
                                    label="POSLEDNI_PRIHLASENI"
                                    name="posledniPrihlaseni"
                                    initialValue={profily?.posledniprihlaseni}
                                    disabled={true}
                                    notRequired={true}
                                />
                            )}
                            {profily?.datumregistrace && (
                                <InputComponent
                                    label="DATUM_REGISTRACE"
                                    name="datumReg"
                                    initialValue={profily?.datumregistrace}
                                    disabled={true}
                                    notRequired={true}
                                />
                            )}
                            <InputComponent
                                label="AUTH_PRVEK"
                                name="typAuth"
                                initialValue={
                                    getLanguageObject(language)['TYP_PRIHLASENI_' + profily?.typprihlaseni] ||
                                    profily?.typprihlaseni
                                }
                                disabled={true}
                                notRequired={true}
                            />
                            {profily?.id && (
                                <InputComponent
                                    label="ID_UCTU"
                                    name="idUcet"
                                    initialValue={profily?.id}
                                    disabled={true}
                                    notRequired={true}
                                />
                            )}
                            {profily?.typprihlaseni === 'CERT' && (
                                <>
                                    <Title level={4} text="MUJ_CERTIFIKAT" dividerTitle={true} />
                                    <InputComponent
                                        label="CERTIFIKAT"
                                        name="cert"
                                        initialValue={profily?.cerdn}
                                        disabled={true}
                                        notRequired={true}
                                    />
                                    <InputComponent
                                        label="EMP_LOGIN_CERTIFIKAT_VYSTAVITEL"
                                        name="issuer"
                                        initialValue={profily?.cerissuer}
                                        disabled={true}
                                        notRequired={true}
                                    />
                                </>
                            )}
                        </>
                    )}
                    <Row className="button-row">
                        <Col>
                            {!isEdit ? (
                                <ButtonComponent
                                    buttontext="ZAVRIT"
                                    htmlType="button"
                                    type="default"
                                    id="zavritBtn"
                                    onClick={() => {
                                        setIsEdit(false);
                                        profily?.typprihlaseni !== 'DLT'
                                            ? navigateWorkaround(routePathEnum.MAIN_PAGE)
                                            : navigateWorkaround('/detail-uctu/PODLT');
                                    }}
                                />
                            ) : (
                                <ButtonComponent
                                    id="zpetBtn"
                                    buttontext="ZPET"
                                    htmlType="button"
                                    type="default"
                                    onClick={() => setIsEdit(false)}
                                />
                            )}
                        </Col>

                        {!isEdit && profily?.typprihlaseni !== 'DLT' ? (
                            <>
                                {profily?.typprihlaseni === 'UZIV' && (
                                    <Col>
                                        <ButtonComponent
                                            onClick={() => setIsEdit(true)}
                                            buttontext="UPRAVIT_UDAJE"
                                            htmlType="button"
                                            id="upravitBtn"
                                        />
                                    </Col>
                                )}
                                {(profily?.typprihlaseni === 'UZIV' || profily?.typprihlaseni === 'PXKLIC') && (
                                    <Col>
                                        <AnchorComponent link={true} to={routePathEnum.ZMENA_HESLA}>
                                            <ButtonComponent
                                                buttontext="ZMENIT_HESLO"
                                                htmlType="button"
                                                id="zmenitHesloBtn"
                                            />
                                        </AnchorComponent>
                                    </Col>
                                )}
                                {profily?.typprihlaseni === 'UZIV' && (
                                    <Col>
                                        <AnchorComponent link={true} to={routePathEnum.ZMENA_EMAILU_PROFIL}>
                                            <ButtonComponent
                                                buttontext="ZMENIT_LOGIN"
                                                htmlType="button"
                                                id="zmenitHesloBtn"
                                            />
                                        </AnchorComponent>
                                    </Col>
                                )}
                                {(profily?.typprihlaseni === 'UZIV' || profily?.typprihlaseni === 'PXKLIC') && (
                                    <Col>
                                        <ButtonComponent
                                            buttontext="ZRUSIT_UCET"
                                            htmlType="button"
                                            onClick={() => setVisible(true)}
                                            id="zrusitUcetBtn"
                                        />
                                    </Col>
                                )}
                            </>
                        ) : (
                            profily?.typprihlaseni !== 'DLT' && (
                                <Col>
                                    <ButtonComponent
                                        buttontext="ODESLAT"
                                        onClick={() => form.submit()}
                                        loading={loading}
                                        id="odeslatBtn"
                                    />
                                </Col>
                            )
                        )}
                    </Row>
                </>
            )}

            <Modal
                title={<TranslateText>ZRUSIT_UCET_TITLE</TranslateText>}
                open={visible}
                onOk={() => formModal.submit()}
                onCancel={() => {
                    setVisible(false);
                    setShowPwErr(false);
                }}
                confirmLoading={loadingModal}
                cancelText={<TranslateText>MUJ_PROFIL_MODAL_ZPET</TranslateText>}
                okText={<TranslateText>ZRUSIT_UCET</TranslateText>}
            >
                <FormWrapper onFinish={onFinishModal} outForm={formModal}>
                    <InputPassword
                        label="HESLO"
                        name="password"
                        type={'password'}
                        extramessage={showPwErr ? 'HESLO_NESPRAVNE' : ''}
                    />
                </FormWrapper>
            </Modal>
        </FormWrapper>
    );
};

export default MujProfil;
